export interface AcquisitionQueueNode {
  getPriority: () => number;
  key: any;
  priority: number;
  resolve: (value: boolean | PromiseLike<boolean>) => void;
}

const MIN_NODE_COMPARATOR: (a: AcquisitionQueueNode, b: AcquisitionQueueNode) => number = (
  a: AcquisitionQueueNode,
  b: AcquisitionQueueNode,
) => a.priority - b.priority;

/**
 * Promise-based prioritized-queue protected by a semaphore for managing access to capacity-limited
 * resources e.g. concurrency-limiting API requests that have some notion of preferred execution order.
 *
 * Example code:
 *     const queryApiPool = new AsyncCountingSemaphoreQueue(10);
 *     if (await queryApiPool.acquire()) {
 *       await fetch(queryApiUrl);
 *       queryApiPool.release();
 *     }
 */
export class AsyncCountingSemaphorePriorityQueue {
  private declare acquisitions: Map<any, AcquisitionQueueNode>;
  private declare count: number;
  private declare dashboardCards: AcquisitionQueueNode[];
  private declare maxCount: number;
  private declare nodeComparator: (a: AcquisitionQueueNode, b: AcquisitionQueueNode) => number;
  private declare queue: Array<AcquisitionQueueNode | undefined>;

  constructor(
    maxCount: number,
    comparator: (a: AcquisitionQueueNode, b: AcquisitionQueueNode) => number = MIN_NODE_COMPARATOR,
  ) {
    if (!Number.isInteger(maxCount) || maxCount < 1) {
      throw new Error(`@maxCount argument must be a finite positive integer`);
    }

    this.acquisitions = new Map<any, AcquisitionQueueNode>();
    this.count = 0;
    this.maxCount = maxCount;
    this.nodeComparator = comparator;
    this.queue = [];
  }

  public acquire(key?: any, getPriority?: () => number): Promise<boolean> {
    const acquisitionKey = key === undefined ? Symbol() : key;
    if (this.acquisitions.has(acquisitionKey)) {
      // acquiring pending keys can cause deadlocks
      throw new Error(`Key "${acquisitionKey}" is already pending`);
    }
    return new Promise((resolve: (value: boolean | PromiseLike<boolean>) => void) => {
      if (!resolve) {
        resolve = (_?: boolean | PromiseLike<boolean>) => {};
      }
      this.pushToQueue(acquisitionKey, resolve, getPriority ? getPriority : () => 0);
      this.tryResolveNextAcquisition();
    });
  }

  public release() {
    if (this.count > 0) {
      this.count -= 1;
      this.tryResolveNextAcquisition();
    }
  }

  public abortPending() {
    while (this.queue.length > 0) {
      const node = this.queue.pop();
      if (!node) {
        continue;
      }
      node.resolve(false);
    }
    this.acquisitions.clear();
  }

  public abortPendingKey(key: any) {
    if (this.acquisitions.has(key)) {
      const node = this.acquisitions.get(key);
      if (!node) {
        return;
      }
      const nodeIndex = this.queue.indexOf(node);

      // Replace the node at nodeIndex with 'undefined' rather than shifting
      // the entire array since we re-order the whole array later anyway.
      this.queue[nodeIndex] = undefined;
      this.acquisitions.delete(key);
      node!.resolve(false);
    }
  }

  public getCount(): number {
    return this.count;
  }

  public getMaxCount(): number {
    return this.maxCount;
  }

  // Number of acquisitions currently waiting for a slot
  public getPendingCount(): number {
    return this.acquisitions.size;
  }

  public setMaxCount(maxCount: number) {
    if (!Number.isInteger(maxCount) || maxCount < 1) {
      throw new Error(`@maxCount argument must be a finite positive integer`);
    }
    this.maxCount = maxCount;
    while (this.tryResolveNextAcquisition()) {
      // On an increase, drain the waiting queue to fill the newly-opened slots.
    }
  }

  private pushToQueue(key: any, resolve: (value: boolean | PromiseLike<boolean>) => void, getPriority: () => number) {
    const node: AcquisitionQueueNode = {
      getPriority,
      key,
      priority: 0,
      resolve,
    };
    this.queue.push(node);
    this.acquisitions.set(key, node);
  }

  private tryResolveNextAcquisition(): boolean {
    if (this.count < this.maxCount && this.acquisitions.size > 0 && this.queue.length > 0) {
      // This is not ideal algorithmic complexity-wise, but even very large
      // dashboards, p99, have fewer than 200 cards, so this shouldn't be very
      // costly in terms of wall-clock time.
      this.queue = this.queue
        .filter((x) => x !== undefined)
        .map((x) => {
          // Priority might have changed. Refreshed in place rather than into a copy: the
          // `acquisitions` map holds references to these same nodes, and abortPendingKey locates a
          // node in `queue` by identity. Re-sorting into copies breaks that identity, so a
          // cancelled node is never removed from `queue` and later consumes a slot via a no-op
          // resolve, permanently stranding capacity.
          x.priority = x.getPriority();
          return x;
        })
        .sort(this.nodeComparator);

      const node = this.queue[0];
      if (!node) {
        return false;
      }

      this.count += 1;
      // Replace the node at nodeIndex with 'undefined' rather than shifting
      // the entire array since we re-order the whole array later anyway.
      this.queue[0] = undefined;
      this.acquisitions.delete(node.key);
      node.resolve(true);
      return true;
    }
    return false;
  }
}
